import { useState } from 'react';
import { SocialIcon, type SocialService } from './SocialIcon';

interface Props {
  /** シェアする URL（絶対）。 */
  readonly url: string;
  /** X などに載せる文。URL は別に付く。 */
  readonly text: string;
  readonly title: string;
  readonly compact?: boolean;
}

/**
 * 投稿画面を開ける SNS（ブラウザから文と URL を渡せるものだけ）。Instagram・TikTok は渡せないので、端末の共有シートに任せる。
 * Facebook は文を受け取らず、URL の OG タグ（/t /r はサーバが差し替える）から投稿を組む。Threads は URL を文に含める。
 */
const TARGETS: readonly { service: SocialService; label: string; href: (url: string, text: string) => string }[] = [
  { service: 'x', label: 'X に投稿', href: (url, text) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
  { service: 'line', label: 'LINE で送る', href: (url, text) => `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
  { service: 'facebook', label: 'Facebook でシェア', href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  { service: 'threads', label: 'Threads に投稿', href: (url, text) => `https://www.threads.com/intent/post?text=${encodeURIComponent(`${text} ${url}`)}` },
];

/**
 * SNS へのシェア。作った診断も、自分の結果も、ここから同じ形で広がる（README §1「友達にシェアして遊ぶ」）。
 * 端末の共有シートがあればそれも出し、SNS は公式ロゴの丸いボタン（名前は読み上げとツールチップ）、最後にリンクのコピー。
 */
export function ShareButtons({ url, text, title, compact = false }: Props) {
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('このリンクをコピーしてください', url);
    }
  };

  return (
    <div className={`share${compact ? ' share--compact' : ''}`}>
      {canNativeShare && (
        <button className="button button--primary" type="button" onClick={() => void navigator.share({ title, text, url }).catch(() => undefined)}>
          シェアする
        </button>
      )}
      {TARGETS.map((t) => (
        <a
          key={t.service}
          className={`button button--ghost button--icon share__${t.service}`}
          href={t.href(url, text)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t.label}
          title={t.label}
        >
          <SocialIcon service={t.service} />
        </a>
      ))}
      <button className="button button--ghost" type="button" onClick={() => void copy()}>
        {copied ? 'コピーしました' : 'リンクをコピー'}
      </button>
    </div>
  );
}
